import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import ProductCard from '../components/product/ProductCard';
import ImageGallery from '../components/product/ImageGallery';
import ReviewList from '../components/review/ReviewList';
import ReviewForm from '../components/review/ReviewForm';
import StarRating from '../components/review/StarRating';
import { getImageUrl } from '../utils/imageUrl';
import { Product, Review } from '../types';
import { productAPI, reviewAPI } from '../services/api';
import { trackFacebookEvent } from '../utils/facebookPixel';
import { gtmEvents } from '../utils/gtmDataLayer';

import './ProductDetails.css';

const ProductDetails: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { addItem: addToWishlist, removeItem: removeFromWishlist, isInWishlist } = useWishlist();
  const [product, setProduct] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  useEffect(() => {
    const loadProduct = async () => {
      try {
        setLoading(true);
        const data = await productAPI.getProductBySlug(slug!);
        setProduct(data);
      } catch (error) {
        console.error('Error loading product:', error);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadProduct();
    }
  }, [slug]);

  const loadReviews = async (productId: number) => {
    try {
      setReviewsLoading(true);
      const data = await reviewAPI.getProductReviews(productId);
      // Ensure data is always an array
      const reviewsArray = Array.isArray(data) ? data : ((data as any)?.results || (data as any)?.data || []);
      setReviews(reviewsArray);
    } catch (error) {
      console.error('Error loading reviews:', error);
      setReviews([]); // Set empty array on error
    } finally {
      setReviewsLoading(false);
    }
  };

  useEffect(() => {
    if (product) {
      loadReviews(product.id);
    }
  }, [product]);

  const currentLang = i18n.language;

  useEffect(() => {
    if (product) {
      const productName = currentLang === 'bn' ? product.name_bn : product.name_en;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const categoryName = (product as any).category ? (currentLang === 'bn' ? (product as any).category.name_bn : (product as any).category.name_en) : '';

      // Track Facebook ViewContent
      trackFacebookEvent('ViewContent', {
        content_name: productName,
        content_ids: [product.id.toString()],
        content_type: 'product',
        value: product.final_price,
        currency: 'BDT',
        content_category: categoryName
      });

      // Track GTM/GA4 view_item
      gtmEvents.viewItem({
        id: product.id,
        name: productName,
        price: product.final_price,
        category: categoryName,
      });
    }
  }, [product, currentLang]);

  const handleAddToCart = () => {
    if (product) {
      addItem(product, quantity);

      // Track AddToCart
      trackFacebookEvent('AddToCart', {
        content_name: currentLang === 'bn' ? product.name_bn : product.name_en,
        content_ids: [product.id.toString()],
        content_type: 'product',
        value: product.final_price * quantity,
        currency: 'BDT'
      });

      Swal.fire({
        // ...
        position: 'top-end',
        icon: 'success',
        title: i18n.language === 'bn' ? 'পণ্য যোগ হয়েছে' : 'Product Added',
        text: t('cart.itemAdded'),
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
        toast: true,
      });
    }
  };

  const handleDirectOrder = () => {
    if (product) {
      // Track AddToCart (since direct order implies adding to cart context)
      trackFacebookEvent('AddToCart', {
        content_name: currentLang === 'bn' ? product.name_bn : product.name_en,
        content_ids: [product.id.toString()],
        content_type: 'product',
        value: product.final_price * quantity,
        currency: 'BDT'
      });

      // Store complete product data in sessionStorage (no need to fetch again)
      const orderData = {
        product: product,
        quantity: quantity,
      };
      sessionStorage.setItem('directOrder', JSON.stringify(orderData));
      navigate('/checkout');
    }
  };

  const handleWishlistToggle = () => {
    if (product) {
      const inWishlist = isInWishlist(product.id);
      if (inWishlist) {
        removeFromWishlist(product.id);
        Swal.fire({
          position: 'top-end',
          icon: 'info',
          title: i18n.language === 'bn' ? 'অপসারিত' : 'Removed',
          text: t('common.removed') || 'Removed from Wishlist',
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
          toast: true,
        });
      } else {
        addToWishlist(product);

        // Track AddToWishlist
        trackFacebookEvent('AddToWishlist', {
          content_name: currentLang === 'bn' ? product.name_bn : product.name_en,
          content_ids: [product.id.toString()],
          content_type: 'product',
          value: product.final_price,
          currency: 'BDT'
        });

        Swal.fire({
          position: 'top-end',
          icon: 'success',
          title: i18n.language === 'bn' ? 'যোগ হয়েছে' : 'Added',
          text: t('common.added') || 'Added to Wishlist',
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
          toast: true,
        });
      }
    }
  };

  const handleQuantityChange = (delta: number) => {
    setQuantity(prev => Math.max(1, Math.min(product?.stock || 1, prev + delta)));
  };

  // Helper function to check if HTML content is actually empty
  const isHtmlEmpty = (html: string | undefined | null): boolean => {
    if (!html || html.trim() === '') return true;
    
    // Remove HTML tags and check if there's actual content
    const text = html.replace(/<[^>]*>/g, '').trim();
    return text === '';
  };

  if (loading) {
    return <div className="loading-container"><p>{t('common.loading')}</p></div>;
  }

  if (!product) {
    return <div className="error-container"><p>{t('common.error')}</p></div>;
  }

  const productName = currentLang === 'bn' ? product.name_bn : product.name_en;
  const productDesc = currentLang === 'bn' ? product.description_bn : product.description_en;
  const productSummary = currentLang === 'bn' ? product.summary_bn : product.summary_en;
  const hasSummaryContent = !isHtmlEmpty(productSummary);
  
  // Get category name - handle both nested category object and category_name string
  const categoryName = typeof product.category === 'object' && product.category !== null
    ? (currentLang === 'bn' ? product.category.name_bn : product.category.name_en)
    : product.category_name || '';

  // Combine main image with gallery images
  const mainImage = product.image ? [{
    id: -1, // Use -1 or distinct ID for main image
    image: getImageUrl(product.image),
    is_primary: true,
    display_order: 0
  }] : [];

  const galleryImages = [
    ...mainImage,
    ...(product.images && product.images.length > 0
      ? product.images.map(img => ({ ...img, image: getImageUrl(img.image) }))
      : [])
  ];

  return (
    <div className="product-details-page">
      <div className="container">
        <div className="breadcrumb">
          <Link to="/">{t('nav.home')}</Link> /
          <Link to="/products">{t('nav.products')}</Link> /
          {productName}
        </div>

        <div className="product-details-layout">
          <div className="product-gallery">
            <ImageGallery images={galleryImages} productName={productName} />
          </div>

          <div className="product-info">
            <div className="product-header">
              <h1 className="product-title">{productName}</h1>
              <button
                className={`wishlist-btn ${isInWishlist(product.id) ? 'active' : ''}`}
                onClick={handleWishlistToggle}
                aria-label="Add to Wishlist"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </button>
            </div>

            <div className="product-pricing">
              <div className="price-row">
                {product.discount_price ? (
                  <>
                    <span className="current-price">৳{product.discount_price}</span>
                    <span className="original-price">৳{product.price}</span>
                    <span className="discount-badge">{product.discount_percentage}%</span>
                  </>
                ) : (
                  <span className="current-price">৳{product.price}</span>
                )}
              </div>
              {reviews.length > 0 && (
                <div className="price-rating-display">
                  <StarRating
                    rating={reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length}
                    readOnly
                    size="small"
                  />
                  <span className="rating-count">
                    ({(reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1)} {currentLang === 'bn' ? 'এর মধ্যে' : 'out of'} 5)
                  </span>
                </div>
              )}
            </div>

            <div className="product-stock">
              {product.in_stock ? (
                <span className="in-stock">✓ {t('product.inStock')}</span>
              ) : (
                <span className="out-of-stock">✗ {t('product.outOfStock')}</span>
              )}
            </div>

            <div className="product-summary">
              <h3>{currentLang === 'bn' ? 'সংক্ষিপ্ত বিবরণ' : 'Summary'}</h3>
              {hasSummaryContent ? (
                <div 
                  className="summary-text"
                  dangerouslySetInnerHTML={{ 
                    __html: productSummary || '' 
                  }}
                />
              ) : (
                <ul className="product-summary-list">
                  <li>
                    <span className="summary-label">{currentLang === 'bn' ? 'বিভাগ:' : 'Category:'}</span>
                    <span className="summary-value">{categoryName}</span>
                  </li>
                  <li>
                    <span className="summary-label">{currentLang === 'bn' ? 'স্টক:' : 'Stock:'}</span>
                    <span className="summary-value">
                      {product.in_stock 
                        ? (currentLang === 'bn' ? 'স্টকে আছে' : 'In Stock') 
                        : (currentLang === 'bn' ? 'স্টক নেই' : 'Out of Stock')
                      }
                    </span>
                  </li>
                  {product.discount_percentage > 0 && (
                    <li>
                      <span className="summary-label">{currentLang === 'bn' ? 'ছাড়:' : 'Discount:'}</span>
                      <span className="summary-value">{product.discount_percentage}%</span>
                    </li>
                  )}
                </ul>
              )}
            </div>

            {product.in_stock && (
              <div className="product-actions">
                <div className="quantity-selector">
                  <label>{t('product.quantity')}:</label>
                  <div className="quantity-controls">
                    <button onClick={() => handleQuantityChange(-1)}>-</button>
                    <span>{quantity}</span>
                    <button onClick={() => handleQuantityChange(1)}>+</button>
                  </div>
                </div>

                <div className="action-buttons-container">
                  <button
                    className="add-to-cart-btn"
                    onClick={handleAddToCart}
                  >
                    {t('buttons.addToCart')}
                  </button>
                  <button
                    className="direct-order-btn"
                    onClick={handleDirectOrder}
                  >
                    {t('buttons.directOrder') || 'অর্ডার করুন'}
                  </button>
                  <button
                    className="whatsapp-btn"
                    onClick={() => {
                      const whatsappNum = sessionStorage.getItem('whatsapp_number') || '8801741818807';
                      window.open(`https://wa.me/${whatsappNum}`, '_blank');
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '8px' }}>
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.008-.57-.008-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.272-.57-.422l-.005.005zM12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm.012 19.822h-.005c-1.956 0-3.874-.527-5.55-1.52L6.46 18.3l-.933-2.616 2.766.732 1.643 1.054c1.536.936 3.483 1.026 5.564-.096 2.08-1.122 3.32-3.32 3.32-5.688 0-3.584-2.916-6.5-6.5-6.5-3.584 0-6.5 2.916-6.5 6.5 0 2.227 1.139 4.195 2.876 5.308l.385.247-.272 1.904-1.156 3.243 3.364-.882 1.895.272c.307.039.619.059.932.059 4.305 0 7.822-3.517 7.822-7.822 0-4.305-3.517-7.822-7.822-7.822z" />
                    </svg>
                    WhatsApp
                  </button>
                  <a
                    href={`tel:${sessionStorage.getItem('support_phone') || '+8801741818807'}`}
                    className="call-now-btn"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '8px' }}>
                      <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 0 0-1.01.24l-1.57 1.97c-2.83-1.44-5.15-3.75-6.59-6.59l1.97-1.57c.26-.26.35-.65.24-1.01A11.36 11.36 0 0 1 8.59 4.2c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1 0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.5c0-.55-.45-1-1-1zM12 3v10l3-3h6V3h-9z" />
                    </svg>
                    {t('buttons.callNow') || (currentLang === 'bn' ? 'কল করুন' : 'Call Now')}
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Full Product Description */}
        <section className="product-full-description">
          <h2>{currentLang === 'bn' ? 'বিস্তারিত বিবরণ' : 'Detailed Description'}</h2>
          <div 
            className="description-content"
            dangerouslySetInnerHTML={{ __html: productDesc || '' }}
          />
        </section>

        {/* Action Buttons Below Description */}
        {product.in_stock && (
          <section className="description-action-buttons">
            <div className="action-buttons-row">
              <button
                className="add-to-cart-btn"
                onClick={handleAddToCart}
              >
                {t('buttons.addToCart')}
              </button>
              <button
                className="direct-order-btn"
                onClick={handleDirectOrder}
              >
                {t('buttons.directOrder') || 'অর্ডার করুন'}
              </button>
              <button
                className="whatsapp-btn"
                onClick={() => {
                  const whatsappNum = sessionStorage.getItem('whatsapp_number') || '8801741818807';
                  window.open(`https://wa.me/${whatsappNum}`, '_blank');
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '8px' }}>
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.008-.57-.008-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.272-.57-.422l-.005.005zM12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm.012 19.822h-.005c-1.956 0-3.874-.527-5.55-1.52L6.46 18.3l-.933-2.616 2.766.732 1.643 1.054c1.536.936 3.483 1.026 5.564-.096 2.08-1.122 3.32-3.32 3.32-5.688 0-3.584-2.916-6.5-6.5-6.5-3.584 0-6.5 2.916-6.5 6.5 0 2.227 1.139 4.195 2.876 5.308l.385.247-.272 1.904-1.156 3.243 3.364-.882 1.895.272c.307.039.619.059.932.059 4.305 0 7.822-3.517 7.822-7.822 0-4.305-3.517-7.822-7.822-7.822z" />
                </svg>
                WhatsApp
              </button>
              <a
                href={`tel:${sessionStorage.getItem('support_phone') || '+8801741818807'}`}
                className="call-now-btn"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '8px' }}>
                  <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 0 0-1.01.24l-1.57 1.97c-2.83-1.44-5.15-3.75-6.59-6.59l1.97-1.57c.26-.26.35-.65.24-1.01A11.36 11.36 0 0 1 8.59 4.2c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1 0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.5c0-.55-.45-1-1-1zM12 3v10l3-3h6V3h-9z" />
                </svg>
                {t('buttons.callNow') || (currentLang === 'bn' ? 'কল করুন' : 'Call Now')}
              </a>
            </div>
          </section>
        )}

        {/* Related Products */}
        {product.related_products && product.related_products.length > 0 && (
          <section className="related-products">
            <h2>{t('product.relatedProducts')}</h2>
            <div className="products-grid">
              {product.related_products.map((relatedProduct) => (
                <ProductCard key={relatedProduct.id} product={relatedProduct} />
              ))}
            </div>
          </section>
        )}

        {/* Reviews Section */}
        <section className="reviews-section">
          <div className="reviews-header">
            <h2>{t('reviews.customerReviews')}</h2>
            {reviews.length > 0 && (
              <div className="reviews-summary">
                <StarRating
                  rating={reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length}
                  readOnly
                  size="small"
                />
                <span className="reviews-count">
                  ({reviews.length} {reviews.length === 1 ? t('reviews.review') : t('reviews.reviews')})
                </span>
              </div>
            )}
          </div>

          <ReviewForm
            productId={product.id}
            onReviewSubmitted={() => loadReviews(product.id)}
          />

          <ReviewList reviews={reviews} loading={reviewsLoading} />
        </section>
      </div>
    </div>
  );
};

export default ProductDetails;
